import { getRouteAccessDeniedMessage } from '@/components/access-control/routeAccessMessages'
import type { ResourceId } from '@/config/access-control'
import { useAccessControl } from '@/hooks/access-control'
import { usePageAccessMessages } from '@/hooks/access-control/usePageAccessMessages'
import { cn } from '@/lib/utils'
import { type PageError, getApiErrorStatus } from '@/utils/errorHandler'
import { CircleDashed, type LucideIcon, ShieldX } from 'lucide-react'
import type React from 'react'
import { useMemo } from 'react'
import { EmptyState, type TabContent } from './empty-state'
import { ErrorState } from './error-state'
import { LoadingState } from './loading-state'

type EmptyReason = 'noData' | 'filtered' | 'search' | 'tab' | 'customView'
type ErrorPolicy = 'always' | 'onlyWhenEmpty'

const GENERIC_FORBIDDEN_MESSAGE =
	'You do not have permission to access this resource. If you believe you should have access, contact your administrator to verify your role permissions.'

interface EmptyStateAction {
	label: string
	onClick: () => void
	icon?: React.ReactNode
	dataTestId?: string
}

export type EmptyMessageOverride = {
	title?: string
	description?: string
}

export interface PageContentAreaProps {
	/**
	 * Ref to the success-case wrapper element — the actual scroll container.
	 * Used by Dashboards to attach a scroll listener for viewport-based fetching.
	 */
	contentRef?: React.Ref<HTMLDivElement>

	/** Controls rendering the loading state. Pages can pass an `isInitialLoad` boolean here. */
	isLoading?: boolean
	/** When set, shows error state depending on `errorPolicy`. */
	error?: PageError | Error | string | null
	/** Total items in the current view (after filters/search/tab). */
	dataLength: number

	/** Prefer showing error only if there is no data. */
	errorPolicy?: ErrorPolicy
	errorTitle?: string
	errorMessage?: string | React.ReactNode
	/** Optional actions (e.g. Retry button) shown in the error state. */
	errorActions?: React.ReactNode

	/** Context for empty-reason detection */
	hasFilters?: boolean
	hasSearch?: boolean
	searchValue?: string
	hasCustomView?: boolean
	currentTab?: string

	/** Loading UI */
	loadingTitle?: string
	loadingDescription?: string
	/** Skeleton shown while `isLoading`, replacing the default centered spinner. */
	loadingSkeleton?: React.ReactNode

	/** Empty UI (base / noData scenario) */
	emptyIcon?: LucideIcon
	emptyTitle?: string
	emptyDescription?: string
	emptyAction?: EmptyStateAction
	emptySecondaryAction?: Omit<EmptyStateAction, 'icon'>
	emptyAdditionalActions?: EmptyStateAction[]
	emptyTabContents?: Record<string, TabContent>
	/** Optional per-reason title/description overrides (e.g. custom filtered-empty copy). */
	emptyMessages?: Partial<Record<EmptyReason, EmptyMessageOverride>>
	emptyContainerHeight?: number | string
	emptyShowBorder?: boolean

	/** Clear callbacks for filtered/search states */
	onClearFilters?: () => void
	onClearSearch?: () => void

	/**
	 * When set, empty list and 403 errors use `/me` grants to distinguish
	 * permission denial from empty API payloads or grant/API mismatch.
	 */
	accessResource?: ResourceId

	/** Wrapper classes for the content area container. */
	className?: string
	dataTestId?: string
	emptyTestId?: string
	clearFiltersTestId?: string
	clearSearchTestId?: string
	children: React.ReactNode
}

function getErrorMessage(error: PageContentAreaProps['error']): string | undefined {
	if (!error) {
		return undefined
	}
	if (typeof error === 'string') {
		return error
	}
	if (error instanceof Error) {
		return error.message
	}
	if (typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
		return error.message
	}
	return String(error)
}

function getEmptyReason({
	hasCustomView,
	hasSearch,
	searchValue,
	hasFilters,
	currentTab,
}: Pick<
	PageContentAreaProps,
	'hasCustomView' | 'hasSearch' | 'searchValue' | 'hasFilters' | 'currentTab'
>): EmptyReason {
	if (hasCustomView) {
		return 'customView'
	}
	if (hasSearch && (searchValue ?? '').trim().length > 0) {
		return 'search'
	}
	if (hasFilters) {
		return 'filtered'
	}
	if (currentTab && currentTab !== 'all') {
		return 'tab'
	}
	return 'noData'
}

export function PageContentArea({
	isLoading,
	error,
	dataLength,
	errorPolicy = 'always',
	errorTitle = 'Something went wrong',
	errorMessage,
	errorActions,
	hasFilters,
	hasSearch,
	searchValue,
	hasCustomView,
	currentTab,
	loadingTitle = 'Loading',
	loadingDescription,
	loadingSkeleton,
	emptyIcon,
	emptyTitle = 'No data available',
	emptyDescription,
	emptyAction,
	emptySecondaryAction,
	emptyAdditionalActions,
	emptyTabContents,
	emptyMessages,
	emptyContainerHeight = '100%',
	emptyShowBorder = false,
	onClearFilters,
	onClearSearch,
	accessResource,
	className,
	dataTestId,
	emptyTestId,
	clearFiltersTestId,
	clearSearchTestId,
	children,
	contentRef,
}: PageContentAreaProps) {
	const viewAccess = useAccessControl(accessResource ? { resource: accessResource, action: 'view' } : {})
	const { emptyListMessage, forbiddenMessage } = usePageAccessMessages(accessResource)
	const isDeniedByViewAccess = accessResource !== undefined && viewAccess.state === 'denied'
	const isEmpty = dataLength === 0
	const shouldShowError =
		Boolean(error && (errorPolicy === 'always' || (errorPolicy === 'onlyWhenEmpty' && isEmpty))) || isDeniedByViewAccess

	const normalizedHasSearch = hasSearch ?? (searchValue ?? '').trim().length > 0
	const emptyReason = getEmptyReason({
		hasCustomView,
		hasSearch: normalizedHasSearch,
		searchValue,
		hasFilters,
		currentTab,
	})

	// 403 is a permission denial → consistent access-restricted treatment, regardless of the page's errorTitle/actions.
	const isForbidden = getApiErrorStatus(error) === 403 || isDeniedByViewAccess
	const forbiddenContextMessage = useMemo(
		() =>
			isForbidden
				? (forbiddenMessage(403) ??
					(accessResource ? getRouteAccessDeniedMessage(accessResource, 'no-permission') : GENERIC_FORBIDDEN_MESSAGE))
				: null,
		[isForbidden, forbiddenMessage, accessResource],
	)

	const resolvedErrorMessage = useMemo(() => {
		if (errorMessage) {
			return errorMessage
		}
		return getErrorMessage(error)
	}, [error, errorMessage])

	const accessAwareEmptyDescription = useMemo(() => {
		const isBaseEmpty = emptyReason === 'noData' || (emptyReason === 'tab' && !emptyTabContents?.[currentTab ?? ''])
		if (isBaseEmpty && accessResource) {
			const accessMsg = emptyListMessage(true)
			if (accessMsg) {
				return accessMsg
			}
		}
		return emptyDescription
	}, [accessResource, currentTab, emptyDescription, emptyListMessage, emptyReason, emptyTabContents])

	if (isLoading) {
		return (
			<div className={cn('flex-1 overflow-hidden size-full', className)}>
				{loadingSkeleton ?? (
					<LoadingState title={loadingTitle} description={loadingDescription} containerClassName='size-full' />
				)}
			</div>
		)
	}

	if (shouldShowError) {
		if (isForbidden) {
			return (
				<div className={cn('flex-1 overflow-hidden size-full', className)}>
					<ErrorState
						icon={ShieldX}
						iconClassName='text-destructive'
						title='Access restricted'
						message={forbiddenContextMessage}
						showSupportEmail={false}
						containerClassName='size-full'
					/>
				</div>
			)
		}
		return (
			<div className={cn('flex-1 overflow-hidden size-full', className)}>
				<ErrorState
					title={errorTitle}
					message={resolvedErrorMessage}
					containerClassName='size-full'
					actions={errorActions}
				/>
			</div>
		)
	}

	if (isEmpty) {
		const EmptyIcon = emptyIcon ?? CircleDashed

		if (emptyReason === 'filtered') {
			const override = emptyMessages?.filtered
			return (
				<div className={cn('flex-1 overflow-hidden size-full', className)}>
					<EmptyState
						icon={EmptyIcon}
						title={override?.title ?? 'No matches found'}
						description={
							override?.description ?? 'No items match your current filters. Try adjusting your filter criteria.'
						}
						action={
							onClearFilters
								? { label: 'Clear filters', onClick: onClearFilters, dataTestId: clearFiltersTestId }
								: undefined
						}
						dataTestId={emptyTestId}
						containerHeight={emptyContainerHeight}
						showBorder={emptyShowBorder}
					/>
				</div>
			)
		}

		if (emptyReason === 'search') {
			const value = (searchValue ?? '').trim()
			const override = emptyMessages?.search
			return (
				<div className={cn('flex-1 overflow-hidden size-full', className)}>
					<EmptyState
						icon={EmptyIcon}
						title={override?.title ?? (value ? `No results for "${value}"` : 'No results found')}
						description={override?.description ?? 'Try a different search term or clear your search.'}
						action={
							onClearSearch
								? { label: 'Clear search', onClick: onClearSearch, dataTestId: clearSearchTestId }
								: undefined
						}
						dataTestId={emptyTestId}
						containerHeight={emptyContainerHeight}
						showBorder={emptyShowBorder}
					/>
				</div>
			)
		}

		if (emptyReason === 'customView') {
			const override = emptyMessages?.customView
			return (
				<div className={cn('flex-1 overflow-hidden size-full', className)}>
					<EmptyState
						icon={EmptyIcon}
						title={override?.title ?? 'No data matches this view'}
						description={override?.description ?? "The saved view filters don't match any current data."}
						containerHeight={emptyContainerHeight}
						showBorder={emptyShowBorder}
						dataTestId={emptyTestId}
					/>
				</div>
			)
		}

		// tab/noData: keep existing tabContents behavior if provided; otherwise fall back to base empty props
		const hasTabOverride = Boolean(emptyTabContents && currentTab && emptyTabContents[currentTab])
		return (
			<div className={cn('flex-1 overflow-hidden size-full', className)}>
				<EmptyState
					icon={EmptyIcon}
					title={hasTabOverride ? undefined : emptyTitle}
					description={hasTabOverride ? undefined : accessAwareEmptyDescription}
					action={emptyAction}
					secondaryAction={emptySecondaryAction}
					additionalActions={emptyAdditionalActions}
					tabContents={emptyTabContents}
					currentTab={hasTabOverride ? currentTab : undefined}
					dataTestId={emptyTestId}
					containerHeight={emptyContainerHeight}
					showBorder={emptyShowBorder}
				/>
			</div>
		)
	}

	return (
		<div
			ref={contentRef}
			data-testid={dataTestId}
			className={cn('size-full flex min-h-0 flex-1 flex-col overflow-hidden', className)}>
			{children}
		</div>
	)
}
